// ============================================================================
// @torre/expenses — Proyección del gasto e ingresos contra egresos (gráficas de Gastos)
// ============================================================================
// Lo que agrega la gráfica «Gasto mes a mes» a lo ya gastado:
//
//   · la PROYECCIÓN de los meses que vienen: por categoría, el presupuesto de
//     ese mes; donde no hay presupuesto, el RITMO de la categoría (#1178: la
//     misma regla que Presupuestos y el ajuste semanal, ver budgetPlan.ts).
//   · INGRESOS CONTRA EGRESOS por mes, con las mismas reglas que Flujo: una
//     devolución no es ingreso, resta de lo gastado.
//
// Lógica pura: el front la calcula con los gastos, presupuestos e ingresos que
// ya pidió al API.
// ============================================================================

import { EXPENSE_CATEGORIES, isFixedCategory, round2, type BudgetLine, type ExpenseCategory, type ExpenseCostType, type Income } from "./index";
import { MAX_GAP_FILL_MONTHS, type CategorySpendMonth } from "./categorySpend";
import { categoryRunRates } from "./budgetPlan";

/** Meses hacia adelante que se proyectan. */
export const PROJECTION_MONTHS = 3;
/** Meses cerrados que se promedian cuando una categoría no tiene presupuesto. */
export const PROJECTION_AVERAGE_MONTHS = 3;

/** De dónde sale lo proyectado de una categoría: su presupuesto o el promedio reciente. */
export type ProjectionSource = "presupuesto" | "promedio";

export interface ProjectedSpendMonth extends CategorySpendMonth {
  /** Parte de `total` que viene de presupuestos. */
  budgeted: number;
  /** Parte de `total` que viene del ritmo reciente de la categoría. */
  averaged: number;
  sources: Partial<Record<ExpenseCategory, ProjectionSource>>;
}

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const monthIndex = (period: string) => Number(period.slice(0, 4)) * 12 + Number(period.slice(5, 7)) - 1;
const periodAt = (index: number) => `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
const currentPeriod = (today: string) => {
  const p = String(today || "").slice(0, 7);
  return PERIOD_RE.test(p) ? p : null;
};

/** Los `count` meses que siguen al mes de `today` (`YYYY-MM-DD` o `YYYY-MM`). */
export function upcomingPeriods(today: string, count = PROJECTION_MONTHS): string[] {
  const current = currentPeriod(today);
  if (!current) return [];
  const base = monthIndex(current);
  return Array.from({ length: Math.max(0, count) }, (_, i) => periodAt(base + i + 1));
}

/**
 * Los `count` meses de CALENDARIO anteriores al mes en curso, del más viejo al
 * más nuevo. Un mes sin movimientos cuenta como cero: el promedio es de los
 * últimos meses de verdad, no de los últimos con datos.
 */
export function closedPeriods(today: string, count = PROJECTION_AVERAGE_MONTHS): string[] {
  const current = currentPeriod(today);
  if (!current) return [];
  const base = monthIndex(current);
  return Array.from({ length: Math.max(0, count) }, (_, i) => periodAt(base - count + i));
}

/**
 * Presupuesto de una categoría en un mes: las líneas mensuales de ese mes; si no
 * hay, la doceava parte de las anuales de ese año. `null` = sin presupuesto.
 */
export function monthlyBudgetFor(budgets: readonly Pick<BudgetLine, "period" | "category" | "amount">[], period: string, category: ExpenseCategory): number | null {
  const amountOf = (b: Pick<BudgetLine, "amount">) => (Number.isFinite(Number(b.amount)) ? Number(b.amount) : 0);
  const monthly = budgets.filter((b) => b.category === category && b.period === period);
  if (monthly.length) return round2(monthly.reduce((s, b) => s + amountOf(b), 0));
  const yearly = budgets.filter((b) => b.category === category && b.period === period.slice(0, 4));
  if (yearly.length) return round2(yearly.reduce((s, b) => s + amountOf(b), 0) / 12);
  return null;
}

/**
 * Proyección del gasto de los meses que vienen, por categoría.
 *
 * Un mes que ya tiene gasto registrado (un pago adelantado, una fecha mal
 * capturada) no se proyecta: no se dibuja dos veces el mismo mes.
 *
 * @param months   gasto real por mes (`categorySpend().months`)
 * @param budgets  líneas de presupuesto de la empresa
 * @param today    `YYYY-MM-DD`; se proyecta a partir del mes siguiente
 * @param costType si la lista está filtrada por tipo de costo: los presupuestos
 *                 solo entran en las categorías de ese tipo; lo demás sale del
 *                 promedio de lo filtrado, que ya es de ese tipo
 */
export function spendProjection(input: {
  months: readonly CategorySpendMonth[];
  budgets: readonly Pick<BudgetLine, "period" | "category" | "amount">[];
  today: string;
  horizon?: number;
  costType?: ExpenseCostType | null;
}): ProjectedSpendMonth[] {
  const { months, budgets, today, horizon = PROJECTION_MONTHS, costType = null } = input;
  const byPeriod = new Map(months.map((m) => [m.period, m]));
  const rates = categoryRunRates(months, today);
  const typeOf = (c: ExpenseCategory): ExpenseCostType => (isFixedCategory(c) ? "fijo" : "variable");
  // Las del sistema y toda categoría propia (#1255) que tenga ritmo o presupuesto.
  const categories = new Set<string>(EXPENSE_CATEGORIES);
  for (const c of Object.keys(rates)) categories.add(c);
  for (const b of budgets) categories.add(b.category);
  /** Ritmo de una categoría (`categoryRunRates`), separado en fijo y variable. */
  const averageOf = (category: ExpenseCategory) => {
    const r = rates[category];
    if (!r) return { fixed: 0, variable: 0 };
    return { fixed: r.fixed, variable: round2(r.rate - r.fixed) };
  };

  return upcomingPeriods(today, horizon)
    .filter((period) => !byPeriod.has(period))
    .map((period) => {
      const out: ProjectedSpendMonth = { period, total: 0, fixed: 0, variable: 0, byCategory: {}, fixedByCategory: {}, budgeted: 0, averaged: 0, sources: {} };
      for (const category of categories) {
        const budget = !costType || typeOf(category) === costType ? monthlyBudgetFor(budgets, period, category) : null;
        let fixed: number;
        let variable: number;
        let source: ProjectionSource;
        if (budget != null) {
          fixed = typeOf(category) === "fijo" ? budget : 0;
          variable = budget - fixed;
          source = "presupuesto";
        } else {
          ({ fixed, variable } = averageOf(category));
          source = "promedio";
        }
        const amount = round2(fixed + variable);
        if (amount <= 0) continue;
        out.byCategory[category] = amount;
        if (fixed > 0) out.fixedByCategory![category] = fixed;
        out.sources[category] = source;
        out.total += amount;
        out.fixed += fixed;
        out.variable += variable;
        out[source === "presupuesto" ? "budgeted" : "averaged"] += amount;
      }
      out.total = round2(out.total);
      out.fixed = round2(out.fixed);
      out.variable = round2(out.variable);
      out.budgeted = round2(out.budgeted);
      out.averaged = round2(out.averaged);
      return out;
    });
}

export interface IncomeVsSpendMonth {
  /** `YYYY-MM`. */
  period: string;
  income: number;
  spend: number;
  /** Ingresos menos egresos. */
  net: number;
  projected: boolean;
}

export interface IncomeVsSpend {
  months: IncomeVsSpendMonth[];
  /** Ingresos sin tipo de cambio: no suman. */
  incomeWithoutRate: number;
}

type IncomeRow = Pick<Income, "period"> & { amountBase?: number | null; personal?: boolean; payerType?: Income["payerType"] };

/**
 * Ingresos contra egresos de la EMPRESA por mes, con las reglas de Flujo
 * (`cashflowSummary`): lo personal no cuenta y una devolución no es ingreso,
 * resta de lo gastado. `months` debe traer solo gasto de la empresa. Los meses
 * proyectados llevan el egreso proyectado y, como ingreso, el promedio de los 3
 * meses cerrados anteriores a hoy.
 */
export function incomeVsSpend(input: {
  months: readonly CategorySpendMonth[];
  incomes: readonly IncomeRow[];
  projection?: readonly CategorySpendMonth[];
  today: string;
}): IncomeVsSpend {
  const { months, incomes, projection = [], today } = input;
  const incomeBy = new Map<string, number>();
  const refundBy = new Map<string, number>();
  let incomeWithoutRate = 0;
  for (const i of incomes) {
    if (i.personal === true || !PERIOD_RE.test(String(i.period))) continue;
    if (i.amountBase == null || !Number.isFinite(i.amountBase)) {
      incomeWithoutRate += 1;
      continue;
    }
    const target = i.payerType === "devolucion" ? refundBy : incomeBy;
    target.set(i.period, (target.get(i.period) || 0) + i.amountBase);
  }
  const spendBy = new Map(months.filter((m) => PERIOD_RE.test(m.period)).map((m) => [m.period, m.total]));

  // Del primer mes con ingreso o gasto al último, sin huecos (con el mismo tope que el gasto).
  const known = [...new Set([...spendBy.keys(), ...incomeBy.keys(), ...refundBy.keys()])].sort();
  const periods: string[] = [];
  if (known.length) {
    const first = monthIndex(known[0]);
    const last = monthIndex(known[known.length - 1]);
    if (last - first < MAX_GAP_FILL_MONTHS) for (let i = first; i <= last; i += 1) periods.push(periodAt(i));
    else periods.push(...known);
  }
  const actual = periods.map((period) => {
    const income = round2(incomeBy.get(period) || 0);
    const spend = round2((spendBy.get(period) || 0) - (refundBy.get(period) || 0));
    return { period, income, spend, net: round2(income - spend), projected: false };
  });

  const incomeOf = new Map(actual.map((m) => [m.period, m.income]));
  const closed = closedPeriods(today);
  const expectedIncome = closed.length ? Math.max(0, round2(closed.reduce((s, p) => s + (incomeOf.get(p) || 0), 0) / closed.length)) : 0;
  const taken = new Set(periods);
  const projected = projection
    .filter((m) => !taken.has(m.period))
    .map((m) => ({ period: m.period, income: expectedIncome, spend: m.total, net: round2(expectedIncome - m.total), projected: true }));

  return { months: [...actual, ...projected], incomeWithoutRate };
}
